// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useRef, useState, useEffectEvent } from 'react';

import { CanceledError } from 'axios';

type InFlightRequest = {
  abortController: AbortController;
  /**
   * When true, the request is aborted as soon as a more recent one is made.
   *
   * Set to `true` only if the API calls are idempotent and can safely be aborted without causing unintended side effects.
   **/
  isSupersedable: boolean;
};

type UseTrackedRequestsReturn = {
  /** Whether any in-flight requests are currently being tracked. */
  loading: boolean;
  /**
   * Tracks an asynchronous request and manages its abort signal.
   * @remarks A request can be aborted after it resolves but before the returned promise settles.
   * Check signal.aborted inside request, in the same callback that performs any side effects;
   * a check anywhere earlier, or a side effect in a .then on the returned promise,
   * can let an aborted response through.
   * Aborted request will throw a {@link CanceledError}.
   */
  trackRequest: <R>(
    /**
     * Any callback that performs an asynchronous request and accepts an abort signal.
     * @param signal The abort signal to use for this request.
     * @returns A promise that resolves with the result of the request.
     */
    request: (signal: AbortSignal) => Promise<R>,
    /**
     * If `true`, this request is aborted as soon as any later request is tracked.
     *
     * Use only if the request is idempotent and can safely be aborted without unintended side effects.
     * @defaultValue false
     **/
    isSupersedable?: boolean,
    /**
     * Abort reason given to the in-flight supersedable requests this call supersedes.
     * @defaultValue 'More recent request made.'
     */
    supersededReason?: string,
  ) => Promise<R>;
  /** Aborts all in-flight requests with the given reason. */
  abortAll: (reason: string) => void;
};

/**
 * Tracks a hook's in-flight requests: gives each one its own abort signal and keeps `loading`
 * true until every request has settled.
 */
export const useTrackedRequests = (
  /** If `true`, aborts every in-flight request when the component using this hook unmounts. */
  abortOnUnmount = false,
  unmountReason = 'Component unmounted.',
): UseTrackedRequestsReturn => {
  const inFlightRequestsRef = useRef(new Set<InFlightRequest>());
  const [loading, setLoading] = useState(false);

  // The reason is stored as a CanceledError so anything rethrowing `signal.reason` (e.g. `signal.throwIfAborted()`) still passes `isCanceledApiRequest`.
  const abortAll = useCallback((reason: string) => {
    inFlightRequestsRef.current.forEach(({ abortController }) => abortController.abort(new CanceledError(reason)));
  }, []);

  // Stored as an effect event to ensure the latest values are used at unmount without adding them as effect dependencies.
  const abortOnUnmountEvent = useEffectEvent(() => {
    if (abortOnUnmount) abortAll(unmountReason);
  });
  useEffect(() => abortOnUnmountEvent, []);

  const trackRequest = useCallback<UseTrackedRequestsReturn['trackRequest']>(
    (request, isSupersedable = false, supersededReason = 'More recent request made.') => {
      const inFlightRequests = inFlightRequestsRef.current;
      inFlightRequests.forEach((inFlight) => {
        if (inFlight.isSupersedable) inFlight.abortController.abort(new CanceledError(supersededReason));
      });

      const tracked: InFlightRequest = { abortController: new AbortController(), isSupersedable };
      inFlightRequests.add(tracked);
      setLoading(true);

      return request(tracked.abortController.signal)
        .catch((e) => {
          tracked.abortController.signal.throwIfAborted();
          throw e;
        })
        .finally(() => {
          inFlightRequests.delete(tracked);
          setLoading(inFlightRequests.size > 0);
        });
    },
    [],
  );

  return { loading, abortAll, trackRequest };
};
